import { ServerConfig } from "@/config/config";
import { Axios } from "@/utils/axios";
import { FixedAsset, IDepreciationPeriod, parseDepreciationPeriod } from "@/models/base/fixed-asset";
import { Purchase } from "@/models/base";

export interface IInvestmentPurchase {
	purchase:Purchase;
	fixedAssetID:number;
}

export interface IBookDepreciationsResult {
	BookYear:number;
	Created:number;
	Updated:number;
	Deleted:number;
}

export interface IManualOpeningGroup {
	GroupID:number;
	Comment:string;
	Lines:number;
}

export interface IOpeningBalanceResult {
	BookYear:number;
	SourceBookYear:number;
	Lines:number;
	TotalDebet:number;
	TotalCredit:number;
	ResultAmount:number;
	ResultLedgerCode:string;
	CentralsOnSuspense:boolean;
	BanksOnSuspense:boolean;
	SaldoDocumentsFound:boolean;
	BeginBanksFound:boolean;
	ManualGroups:IManualOpeningGroup[];
	Warnings:string[];
	LastOpeningBalanceDate:Date | null;
}

export interface IUpdateBookYearResult {
	PreviousDepreciations:IBookDepreciationsResult | null;
	OpeningBalance:IOpeningBalanceResult | null;
	Depreciations:IBookDepreciationsResult | null;
}

function parseOpeningBalanceResult(data:any):IOpeningBalanceResult {
	return {
		...data,
		ManualGroups: data.ManualGroups || [],
		Warnings: data.Warnings || [],
		LastOpeningBalanceDate: data.LastOpeningBalanceDate ? new Date(data.LastOpeningBalanceDate) : null
	};
}

export class FixedAssetServiceClass {
	get url():string {
		return ServerConfig.host + "/fixed-assets";
	}

	async getAll():Promise<FixedAsset[]> {
		let result = await Axios.get(this.url);
		return (result.data || []).map((d:any) => new FixedAsset(d));
	}

	async get(id:number):Promise<FixedAsset> {
		let result = await Axios.get(`${this.url}/${id}`);
		return new FixedAsset(result.data);
	}

	async getSchedule(id:number):Promise<IDepreciationPeriod[]> {
		let result = await Axios.get(`${this.url}/${id}/schedule`);
		return (result.data || []).map(parseDepreciationPeriod);
	}

	async previewSchedule(asset:FixedAsset):Promise<IDepreciationPeriod[]> {
		let result = await Axios.post(`${this.url}/schedule-preview`, asset.getJSON());
		return (result.data || []).map(parseDepreciationPeriod);
	}

	async save(asset:FixedAsset, bookNow:boolean):Promise<{asset:FixedAsset, bookResult:IBookDepreciationsResult | null}> {
		let params = {book: bookNow ? "1" : "0"};
		let result = asset.ID ? await Axios.put(this.url, asset.getJSON(), {params}) : await Axios.post(this.url, asset.getJSON(), {params});
		return {
			asset: new FixedAsset(result.data.Asset),
			bookResult: result.data.BookResult || null
		};
	}

	async delete(id:number):Promise<void> {
		await Axios.delete(`${this.url}/${id}`);
	}

	async getInvestmentPurchases(onlyMissing:boolean):Promise<IInvestmentPurchase[]> {
		let result = await Axios.get(`${this.url}/investment-purchases`, {params: {onlyMissing: onlyMissing ? "1" : "0"}});
		return (result.data || []).map((d:any) => {
			return {
				purchase: new Purchase(d),
				fixedAssetID: d.FixedAssetID || 0
			};
		});
	}

	async bookDepreciations(bookYear:number):Promise<IBookDepreciationsResult> {
		let result = await Axios.post(`${this.url}/book/${bookYear}`, {});
		return result.data;
	}

	async getOpeningBalanceStatus(bookYear:number):Promise<IOpeningBalanceResult> {
		let result = await Axios.get(`${this.url}/opening-balance/${bookYear}`);
		return parseOpeningBalanceResult(result.data);
	}

	async createOpeningBalance(bookYear:number):Promise<IOpeningBalanceResult> {
		let result = await Axios.post(`${this.url}/opening-balance/${bookYear}`, {});
		return parseOpeningBalanceResult(result.data);
	}

	async updateBookYear(bookYear:number, depreciations:boolean, openingBalance:boolean):Promise<IUpdateBookYearResult> {
		let result = await Axios.post(`${this.url}/update-bookyear/${bookYear}`, {Depreciations: depreciations, OpeningBalance: openingBalance});
		return {
			PreviousDepreciations: result.data.PreviousDepreciations || null,
			OpeningBalance: result.data.OpeningBalance ? parseOpeningBalanceResult(result.data.OpeningBalance) : null,
			Depreciations: result.data.Depreciations || null
		};
	}

	async deleteManualOpeningGroup(bookYear:number, groupId:number):Promise<void> {
		await Axios.delete(`${this.url}/opening-balance/${bookYear}/manual/${groupId}`);
	}
}

export const FixedAssetService = new FixedAssetServiceClass();
